// src/components/answer/AnswerMath.jsx

import React, { useMemo } from "react";
import { BlockMath } from "react-katex";
import "katex/dist/katex.min.css";
import AnswerText from "./AnswerText";

/**
 * AnswerMath
 *
 * Production-grade single-file math / science / finance formula renderer.
 *
 * Uses:
 * - KaTeX through react-katex for professional equation rendering.
 * - Safe fallback rendering if a formula is not valid LaTeX.
 *
 * Purpose:
 * - Display algebra, calculus, finance/accounting formulas, chemistry equations,
 *   physics equations, statistics/probability notation, and calculation steps.
 * - Keep the app stable: bad math should never create a blank card.
 * - Make Lexa's plain-text formulas look professional even when she does not output
 *   perfect LaTeX.
 *
 * Important:
 * - This component is a DISPLAY renderer, not a calculator/solver.
 * - Lexa/OpenAI produces the reasoning and calculations.
 * - KaTeX displays the formulas beautifully.
 *
 * Supported examples:
 *
 * [math] A = P(1 + r/n)^(nt)
 * [math] ROI = (Net Profit / Investment Cost) * 100
 * [math] Assets = Liabilities + Equity
 * [math] y = mx + b
 * [math] x = (-b ± sqrt(b^2 - 4ac)) / 2a
 * [math] d/dx x^2 = 2x
 * [math] ∫ x^2 dx = x^3 / 3 + C
 * [math] lim x->0 sin(x)/x = 1
 * [math] H2 + O2 -> H2O
 * [math] N2 + 3H2 <-> 2NH3
 * [math] PV = nRT
 * [math] F = ma
 * [math] E = mc^2
 */

const TEXT_DARK = "#0f172a";
const TEXT_MUTED = "#475569";

const FINANCE_TERMS = [
  "roi",
  "gross margin",
  "net margin",
  "profit margin",
  "markup",
  "discount",
  "interest",
  "compound",
  "present value",
  "future value",
  "pv",
  "fv",
  "npv",
  "irr",
  "ebitda",
  "depreciation",
  "amortization",
  "basis",
  "tax",
  "rate",
  "revenue",
  "cogs",
  "cash flow",
  "break even",
  "breakeven",
  "assets",
  "liabilities",
  "equity",
  "debit",
  "credit",
  "working capital",
];

const CALCULUS_TERMS = [
  "d/dx",
  "dy/dx",
  "derivative",
  "integral",
  "\\int",
  "∫",
  "lim",
  "limit",
  "dx",
  "∂",
  "partial",
  "gradient",
  "nabla",
];

const CHEMISTRY_TERMS = [
  "molar",
  "mole",
  "reaction",
  "equilibrium",
  "acid",
  "base",
  "ph",
  "h2o",
  "co2",
  "o2",
  "h2",
  "n2",
  "nh3",
  "nacl",
  "mol",
  "stoichiometry",
  "kc",
  "kp",
  "delta g",
  "gibbs",
  "le chatelier",
  "le châtelier",
];

const PHYSICS_TERMS = [
  "force",
  "velocity",
  "acceleration",
  "energy",
  "momentum",
  "gravity",
  "mass",
  "newton",
  "kinetic",
  "potential",
  "voltage",
  "current",
  "resistance",
  "ohm",
  "coulomb",
  "frequency",
  "wavelength",
  "pressure",
  "density",
  "pv=nrt",
  "e=mc",
  "f=ma",
];

const STATS_TERMS = [
  "probability",
  "variance",
  "standard deviation",
  "mean",
  "median",
  "mode",
  "z-score",
  "z score",
  "confidence interval",
  "normal distribution",
  "bayes",
  "expected value",
  "p(",
  "σ",
  "mu",
];

const GREEK_LATEX_MAP = [
  // Uppercase variants first; otherwise /gi lowercase entries consume them.
  [/\bDelta\b/g, "\\Delta"],
  [/\bSigma\b/g, "\\Sigma"],
  [/\bOmega\b/g, "\\Omega"],
  [/\balpha\b/gi, "\\alpha"],
  [/\bbeta\b/gi, "\\beta"],
  [/\bgamma\b/gi, "\\gamma"],
  [/\bdelta\b/gi, "\\delta"],
  [/\btheta\b/gi, "\\theta"],
  [/\blambda\b/gi, "\\lambda"],
  [/\bmu\b/gi, "\\mu"],
  [/\bsigma\b/gi, "\\sigma"],
  [/\bomega\b/gi, "\\omega"],
  [/\bpi\b/gi, "\\pi"],
  [/\brho\b/gi, "\\rho"],
  [/\bphi\b/gi, "\\phi"],
];

const LATEX_SYMBOL_MAP = [
  // Longest tokens first so shorter replacements cannot corrupt them.
  [/<=>/g, "\\rightleftharpoons"],
  [/<->/g, "\\rightleftharpoons"],
  [/-->/g, "\\to"],
  [/<=/g, "\\le"],
  [/>=/g, "\\ge"],
  [/!=/g, "\\ne"],
  [/\+\/-/g, "\\pm"],
  [/->/g, "\\to"],
  [/<-/g, "\\leftarrow"],
  [/≈/g, "\\approx"],
  [/≤/g, "\\le"],
  [/≥/g, "\\ge"],
  [/≠/g, "\\ne"],
  [/±/g, "\\pm"],
  [/∞/g, "\\infty"],
  [/∫/g, "\\int"],
  [/∂/g, "\\partial"],
  [/∑/g, "\\sum"],
  [/×/g, "\\times"],
  [/÷/g, "\\div"],
];

function stripOuterMarker(value) {
  return String(value || "")
    .replace(
      /^\s*\[(math|formula|equation|chemistry|finance|accounting|calculation|physics|science|statistics|probability)\]\s*/i,
      ""
    )
    .trim();
}

function stripLatexDelimiters(value) {
  return String(value || "")
    .replace(/^\s*\$\$/g, "")
    .replace(/\$\$\s*$/g, "")
    .replace(/^\s*\\\[/g, "")
    .replace(/\\\]\s*$/g, "")
    .replace(/^\s*\\\(/g, "")
    .replace(/\\\)\s*$/g, "")
    .trim();
}


const MATH_FILE_EXTENSION_RE =
  /\.(?:pdf|docx?|xlsx?|pptx?|csv|txt|rtf|odt|ods|odp|pages|numbers|key|json|xml|html?|md|zip|png|jpe?g|gif|webp|heic|heif|svg)\b/i;

const MATH_PATH_RE =
  /(?:[A-Za-z]:\\(?:[^\\\r\n]+\\)*[^\\\r\n]*|(?:^|\s)\/(?:[^/\s]+\/)+[^/\s]*)/;

const MATH_EMAIL_RE =
  /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i;

const MATH_DATE_RE =
  /\b(?:\d{4}-\d{1,2}-\d{1,2}|\d{1,2}[/-]\d{1,2}[/-]\d{2,4})\b/;

const EXPLICIT_LATEX_COMMAND_RE =
  /\\(?:frac|dfrac|tfrac|sqrt|sum|prod|int|iint|iiint|lim|log|ln|sin|cos|tan|arcsin|arccos|arctan|exp|left|right|begin|end|matrix|pmatrix|bmatrix|cases|alpha|beta|gamma|delta|Delta|epsilon|theta|lambda|mu|sigma|Sigma|phi|psi|omega|Omega|pi|rho|infty|partial|nabla|cdot|times|div|pm|mp|leq?|geq?|neq|approx|equiv|propto|text|mathrm)\b/;

const MATH_RELATION_RE =
  /(?:=|≈|≠|≤|≥|<=>|<->|=>|<=|>=|\\leq?|\\geq?|\\neq|\\approx|\\equiv|\\propto)/;

const MATH_MINUS_RE =
  /(?:\b[A-Za-z]\b|\b[A-Z]{2,8}\b|\d+(?:\.\d+)?|[)\]}])\s*-\s*(?:\b[A-Za-z]\b|\b[A-Z]{2,8}\b|\d+(?:\.\d+)?|[(\[{])/;

const MATH_OPERATOR_RE =
  /(?:\+|[×÷·]|\\times\b|\\cdot\b|\\div\b|\*|\/)/;

const MATH_PAGE_REFERENCE_RE =
  /\b(?:page|pages|p\.?)\s*#?\s*\d+(?:\s*(?:-|–|—|to|\/)\s*\d+)?\b/gi;

const MATH_POWER_RE =
  /(?:\^\{[^}]+\}|\^[-+]?[A-Za-z0-9()]+)/;

const MATH_SUBSCRIPT_RE =
  /(?:_\{[^}]+\}|\b[A-Za-z]{1,5}_[A-Za-z0-9]{1,6}\b)/;

const MATH_FUNCTION_RE =
  /\b(?:f|g|h|P|PV|FV|NPV|IRR|ROI|APR|r|x|y|z|sin|cos|tan|log|ln|exp|sqrt|max|min)\s*\([^)]*\)/i;

const MATH_SYMBOL_RE =
  /[∑∫√∞πθλμσΔΣΩρφ∇±∓∂]/;

const CURRENCY_AMOUNT_RE =
  /(?:[$€£¥]\s*\d|\b(?:USD|EUR|GBP|JPY)\s+\d)/i;

const CHEMICAL_REACTION_RE =
  /\b(?:\d*)[A-Z][a-z]?\d*(?:\([A-Z][A-Za-z0-9]*\)\d*)?(?:\s*\+\s*(?:\d*)[A-Z][a-z]?\d*(?:\([A-Z][A-Za-z0-9]*\)\d*)?)+\s*(?:<=>|<->|->|→|⇌)\s*(?:\d*)[A-Z][A-Za-z0-9()]*/;

const CHEMICAL_ARROW_RE =
  /\b(?:\d*)[A-Z][A-Za-z0-9()]*\s*(?:<=>|<->|->|→|⇌)\s*(?:\d*)[A-Z][A-Za-z0-9()]*/;

const KNOWN_MATH_LHS_RE =
  /^(?:[A-Za-z]|PV|FV|NPV|IRR|ROI|APR|COGS|EBITDA|P|r|n|t|x|y|z)(?:_\{?[A-Za-z0-9]+\}?)?$/i;

function hasExplicitMathEnvelope(value) {
  const raw = String(value || "").trim();
  return (
    /^\s*\\\[[\s\S]*\\\]\s*$/.test(raw) ||
    /^\s*\$\$[\s\S]*\$\$\s*$/.test(raw) ||
    /^\s*\\\([\s\S]*\\\)\s*$/.test(raw)
  );
}

function countNaturalWords(value) {
  return (String(value || "").match(/\b[A-Za-z]{3,}\b/g) || []).length;
}

function looksLikeObviousNonMath(value) {
  const text = String(value || "").trim();
  if (!text) return true;

  if (/https?:\/\//i.test(text)) return true;
  if (MATH_EMAIL_RE.test(text)) return true;
  if (MATH_FILE_EXTENSION_RE.test(text)) return true;
  if (MATH_PATH_RE.test(text)) return true;

  if (
    MATH_DATE_RE.test(text) &&
    !(MATH_RELATION_RE.test(text) && MATH_OPERATOR_RE.test(text))
  ) {
    return true;
  }

  // A currency symbol is not a math delimiter. Currency prose stays prose
  // unless the same line contains genuine arithmetic/equality/power structure.
  if (
    CURRENCY_AMOUNT_RE.test(text) &&
    !(
      MATH_RELATION_RE.test(text) ||
      MATH_OPERATOR_RE.test(text) ||
      MATH_POWER_RE.test(text)
    )
  ) {
    return true;
  }

  return false;
}

function looksLikeMathExpressionLine(value) {
  const text = String(value || "").trim();
  if (!text) return false;
  if (looksLikeObviousNonMath(text)) return false;

  if (EXPLICIT_LATEX_COMMAND_RE.test(text)) return true;
  if (CHEMICAL_REACTION_RE.test(text)) return true;
  if (CHEMICAL_ARROW_RE.test(text)) return true;

  /*
    Strip document provenance such as "(Page 1)" before computing math signals.
    Page references must never contribute parentheses/digits to the math score.
  */
  const mathSignalText =
    text.replace(
      MATH_PAGE_REFERENCE_RE,
      " "
    );

  const hasRelation =
    MATH_RELATION_RE.test(
      mathSignalText
    );

  const hasOperator =
    MATH_OPERATOR_RE.test(
      mathSignalText
    ) ||
    MATH_MINUS_RE.test(
      mathSignalText
    );

  const hasPower =
    MATH_POWER_RE.test(
      mathSignalText
    );

  const hasSubscript =
    MATH_SUBSCRIPT_RE.test(
      mathSignalText
    );

  const hasFunction =
    MATH_FUNCTION_RE.test(
      mathSignalText
    );

  const hasSymbol =
    MATH_SYMBOL_RE.test(
      mathSignalText
    );

  const hasDigit =
    /\d/.test(
      mathSignalText
    );

  const hasParen =
    /[()[\]{}]/.test(
      mathSignalText
    );

  const wordCount =
    countNaturalWords(
      text
    );

  /*
    Long natural-language prose is not an equation merely because it contains
    punctuation, a year, a slash, a dash, or a parenthetical page reference.
  */
  if (
    wordCount >= 6 &&
    !hasRelation &&
    !hasPower &&
    !hasSubscript &&
    !hasFunction &&
    !hasSymbol
  ) {
    return false;
  }

  // Compact formulas: x = 2, E = mc^2, PV = FV/(1+r)^n.
  const compactEquation = text.match(
    /^\s*([A-Za-z][A-Za-z0-9_]{0,10}(?:\([^)]*\))?)\s*(=|≈|≠|≤|≥|<=|>=)\s*(.+?)\s*$/
  );

  if (compactEquation) {
    const lhs = String(compactEquation[1] || "");
    const rhs = String(compactEquation[3] || "");

    const rhsHasMath =
      /\d/.test(rhs) ||
      MATH_OPERATOR_RE.test(rhs) ||
      MATH_POWER_RE.test(rhs) ||
      MATH_SUBSCRIPT_RE.test(rhs) ||
      MATH_FUNCTION_RE.test(rhs) ||
      MATH_SYMBOL_RE.test(rhs) ||
      EXPLICIT_LATEX_COMMAND_RE.test(rhs);

    if (
      KNOWN_MATH_LHS_RE.test(lhs) ||
      rhsHasMath
    ) {
      return true;
    }
  }

  // Readable business/science formulas:
  // Gross Margin = (Revenue - COGS) / Revenue
  // Assets = Liabilities + Equity
  const phraseEquation = text.match(
    /^\s*([A-Za-z][A-Za-z0-9 ()/%.-]{0,64})\s*(=|≈|≠|≤|≥|<=|>=)\s*(.+?)\s*$/
  );

  if (phraseEquation) {
    const rhs = String(phraseEquation[3] || "");

    if (
      (
        MATH_OPERATOR_RE.test(rhs) ||
        /\s-\s/.test(rhs)
      ) ||
      MATH_POWER_RE.test(rhs) ||
      MATH_SUBSCRIPT_RE.test(rhs) ||
      MATH_FUNCTION_RE.test(rhs) ||
      MATH_SYMBOL_RE.test(rhs) ||
      EXPLICIT_LATEX_COMMAND_RE.test(rhs) ||
      /\d/.test(rhs)
    ) {
      return true;
    }
  }

  if (hasFunction && wordCount <= 2) return true;

  if (
    hasRelation &&
    (hasOperator || hasPower || hasSubscript || hasFunction || hasSymbol || hasDigit)
  ) {
    return true;
  }

  const structuralSignals = [
    hasOperator,
    hasPower,
    hasSubscript,
    hasFunction,
    hasSymbol,
    hasParen && hasDigit,
  ].filter(Boolean).length;

  if (structuralSignals >= 2 && wordCount <= 5) return true;

  if (
    hasDigit &&
    hasOperator &&
    wordCount <= 2 &&
    (
      hasPower ||
      hasParen ||
      wordCount <= 1
    )
  ) {
    return true;
  }

  return false;
}

function looksLikeMathNoteLine(value) {
  const text = String(value || "").trim();
  if (!text) return false;

  return /^\s*(?:given|where|assume|let|step\s*\d+|solution|formula|therefore|answer|result|final|conclusion|because|using|then)\b/i.test(
    text
  );
}

function isRenderableMathContent(rawValue, normalizedContent) {
  const original = String(rawValue || "").trim();
  const content = String(normalizedContent || "").trim();

  if (!content) return false;

  // Deliberate TeX fences remain strong evidence, including very short math.
  if (hasExplicitMathEnvelope(original)) return true;

  const lines = splitMathLines(content);
  if (!lines.length) return false;

  return lines.some((line) =>
    looksLikeMathExpressionLine(line)
  );
}

function splitMathLines(content) {
  /*
    Accept normal [math] payloads and raw LaTeX display blocks:
      \\[
      x=\\frac{...}{...}
      \\]

    The old version split delimiter lines into separate rows. That made the UI
    show broken \\[ and \\] lines when AnswerRenderer did not strip them.
    This parser removes fences and groups display math content as one KaTeX row.
  */
  const lines = String(content || "")
    .replace(/\r\n/g, "\n")
    .split(/\n/);

  const out = [];
  let collecting = false;
  let buffer = [];

  function flushBuffer() {
    const value = buffer.join(" ").trim();
    if (value) out.push(value);
    buffer = [];
  }

  for (const rawLine of lines) {
    const line = String(rawLine || "").trim();
    if (!line) {
      if (collecting) buffer.push("");
      continue;
    }

    const oneLineDisplay = line.match(/^\s*(?:\\\[([\s\S]+?)\\\]|\$\$([\s\S]+?)\$\$)\s*$/);
    if (oneLineDisplay) {
      const value = String(oneLineDisplay[1] || oneLineDisplay[2] || "").trim();
      if (value) out.push(value);
      continue;
    }

    const oneLineParen = line.match(/^\s*\\\(([\s\S]+?)\\\)\s*$/);
    if (oneLineParen) {
      const value = String(oneLineParen[1] || "").trim();
      if (value) out.push(value);
      continue;
    }

    if (/^\s*(?:\\\[|\$\$)\s*$/.test(line)) {
      collecting = true;
      buffer = [];
      continue;
    }

    if (/^\s*(?:\\\]|\$\$)\s*$/.test(line)) {
      collecting = false;
      flushBuffer();
      continue;
    }

    if (collecting) {
      buffer.push(line);
      continue;
    }

    const clean = stripLatexDelimiters(line);
    if (clean) out.push(clean);
  }

  if (collecting) flushBuffer();

  return out.filter(Boolean);
}

function escapeLatexText(value) {
  return String(value || "")
    .replace(/\\/g, "\\textbackslash ")
    .replace(/&/g, "\\&")
    .replace(/%/g, "\\%")
    .replace(/#/g, "\\#")
    .replace(/\$/g, "\\$")
    .replace(/_/g, "\\_");
}

function isLikelyLatex(line) {
  return /\\[a-zA-Z]+|\\frac|\\sqrt|\\int|\\sum|\\lim|\\left|\\right|_\{|_\w|\^\{|[{}]/.test(line);
}

function normalizePlainToLatex(line) {
  let out = stripLatexDelimiters(line)
    .replace(/\*\*/g, "")
    .replace(/__/g, "")
    .replace(/\t/g, " ")
    .trim();

  if (!out) return "";

  // Preserve existing LaTeX commands where present.
  const alreadyLatex = isLikelyLatex(out);

  for (const [pattern, replacement] of GREEK_LATEX_MAP) {
    out = out.replace(pattern, replacement);
  }

  for (const [pattern, replacement] of LATEX_SYMBOL_MAP) {
    out = out.replace(pattern, replacement);
  }

  // Common text words to symbols.
  out = out.replace(/\bsqrt\s*\(([^()]*)\)/gi, "\\sqrt{$1}");
  out = out.replace(/\binfinity\b/gi, "\\infty");
  out = out.replace(/\binf\b/gi, "\\infty");
  out = out.replace(/\btimes\b/gi, "\\times");
  out = out.replace(/\bdivided by\b/gi, "\\div");
  out = out.replace(/\bDelta\s*G\b/gi, "\\Delta G");

  // Basic derivative/integral cleanup.
  out = out.replace(/\bd\/dx\b/gi, "\\frac{d}{dx}");
  out = out.replace(/\bdy\/dx\b/gi, "\\frac{dy}{dx}");
  out = out.replace(/\blim\s+([a-zA-Z])\s*\\to\s*([^\s]+)\s+/gi, "\\lim_{$1 \\to $2} ");

  // Convert simple fractions around equals or formula pieces:
  // ROI = (Net Profit / Investment Cost) * 100
  // Keep word formulas readable by wrapping words in \text{} if no math variables.
  out = out.replace(/\s+\*\s+/g, " \\times ");

  // Convert common chemical formulas to LaTeX subscripts.
  out = convertChemicalFormulasToLatex(out);

  // Convert exponent syntax x^2, (1+r)^n, cm^-3.
  out = out.replace(/\^(-?\d+|[a-zA-Z])\b/g, "^{$1}");
  out = out.replace(/\^\(([^)]+)\)/g, "^{$1}");

  // Convert explicit underscore syntax x_1 to x_{1}.
  out = out.replace(/_([A-Za-z0-9]+)\b/g, "_{$1}");

  // Make plain finance/accounting word formulas more readable in KaTeX.
  if (!alreadyLatex && hasMostlyWords(out)) {
    out = convertWordFormulaToLatex(out);
  }

  return out.trim();
}

function hasMostlyWords(value) {
  const letters = (String(value).match(/[A-Za-z]/g) || []).length;
  const operators = (String(value).match(/[=+\-*/()]/g) || []).length;
  return letters > 12 && operators >= 1;
}

function convertWordFormulaToLatex(value) {
  /*
    Converts formulas like:
      Gross Margin = (Revenue - COGS) / Revenue
    into:
      \text{Gross Margin} = \frac{\text{Revenue} - \text{COGS}}{\text{Revenue}}
    This is intentionally conservative.
  */
  let out = String(value || "");

  // Replace parenthesized simple division with \frac{}{}.
  out = out.replace(/\(([^()]+)\)\s*\/\s*([A-Za-z][A-Za-z\s.-]*)/g, (_, numerator, denominator) => {
    return `\\frac{${wrapWordsInText(numerator)}}{${wrapWordsInText(denominator)}}`;
  });

  // Replace simple A / B if not already a command.
  out = out.replace(/([A-Za-z][A-Za-z\s.-]*?)\s*\/\s*([A-Za-z][A-Za-z\s.-]*)/g, (_, numerator, denominator) => {
    if (numerator.includes("\\") || denominator.includes("\\")) return `${numerator}/${denominator}`;
    return `\\frac{${wrapWordsInText(numerator)}}{${wrapWordsInText(denominator)}}`;
  });

  out = wrapWordsAroundOperators(out);
  return out;
}

function wrapWordsInText(value) {
  return String(value || "")
    .trim()
    .split(/(\s*[+\-]\s*)/)
    .map((part) => {
      if (/^\s*[+\-]\s*$/.test(part)) return part.trim() === "+" ? " + " : " - ";
      const clean = part.trim();
      if (!clean) return "";
      if (/^\\/.test(clean)) return clean;
      if (/^[A-Z]{2,}$/.test(clean)) return `\\text{${escapeLatexText(clean)}}`;
      if (/[A-Za-z]/.test(clean)) return `\\text{${escapeLatexText(clean)}}`;
      return clean;
    })
    .join("");
}

function wrapWordsAroundOperators(value) {
  return String(value || "")
    .split(/(=|\\times|\+|\-)/)
    .map((part) => {
      const trimmed = part.trim();
      if (!trimmed) return part;
      if (/^(=|\\times|\+|\-)$/.test(trimmed)) return ` ${trimmed} `;
      if (/\\text\{|\\frac\{|\\sqrt\{|\\[a-zA-Z]+/.test(trimmed)) return trimmed;
      if (/[A-Za-z][A-Za-z\s.-]+/.test(trimmed) && !/[0-9][A-Za-z]/.test(trimmed)) {
        return `\\text{${escapeLatexText(trimmed)}}`;
      }
      return trimmed;
    })
    .join("")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function convertChemicalFormulasToLatex(value) {
  let out = String(value || "");

  /*
    Convert chemical formulas:
      H2O -> H_{2}O
      CO2 -> CO_{2}
      N2 + 3H2 -> 2NH3
      Ca(OH)2 -> Ca(OH)_{2}
    Avoid converting ordinary years by requiring an element-like token.
  */
  out = out.replace(/([A-Z][a-z]?|\))(\d+)/g, (_, atom, digits) => `${atom}_{${digits}}`);

  // Simple charge notation: SO4^2- -> SO_{4}^{2-}
  out = out.replace(/\^\{?(\d*[+-])\}?/g, "^{$1}");

  return out;
}

function detectMathKind(content) {
  const lower = String(content || "").toLowerCase();

  if (CHEMISTRY_TERMS.some((term) => lower.includes(term))) return "Chemistry / Reaction";
  if (PHYSICS_TERMS.some((term) => lower.includes(term))) return "Physics Formula";
  if (FINANCE_TERMS.some((term) => lower.includes(term))) return "Finance / Accounting Formula";
  if (CALCULUS_TERMS.some((term) => lower.includes(term))) return "Calculus";
  if (STATS_TERMS.some((term) => lower.includes(term))) return "Statistics / Probability";
  if (/[a-z]\s*=\s*[-+*/^().0-9a-z\s]+/i.test(content)) return "Formula";
  if (/\d+\s*[+\-×*/÷]\s*\d+/.test(content)) return "Calculation";

  return "Math";
}

function getLineClass(line) {
  const value = String(line || "");

  if (/^(therefore|∴|answer|result|final|conclusion)\b/i.test(value)) {
    return "answer-math-line answer-math-line-result";
  }

  if (/^\s*(given|where|assume|let|step\s*\d+|solution|formula)\b/i.test(value)) {
    return "answer-math-line answer-math-line-note";
  }

  if (/\\rightleftharpoons|\\to|→|⇌/.test(value)) {
    return "answer-math-line answer-math-line-reaction";
  }

  if (/=|\\approx|\\le|\\ge|\\ne/.test(value)) {
    return "answer-math-line answer-math-line-equation";
  }

  return "answer-math-line";
}

function tokenizeFallback(text) {
  const parts = [];
  const source = String(text || "");

  const re =
    /(\\frac\{[^}]*\}\{[^}]*\}|\\sqrt\{[^}]*\}|\\[a-zA-Z]+|\b\d+(?:\.\d+)?(?:e[-+]?\d+)?%?\b|[A-Za-z][A-Za-z0-9_\-]*|\+|\-|×|\\times|\/|÷|\\div|=|≈|≤|≥|≠|±|→|←|⇌|↔|∫|∂|∇|∑|Σ|π|∞|Δ|δ|θ|α|β|γ|λ|μ|σ|ω|Ω|ρ|φ|\(|\)|\[|\]|\{|\}|,|;|:)/g;

  let last = 0;
  let match;

  while ((match = re.exec(source)) !== null) {
    if (match.index > last) {
      parts.push({ type: "text", value: source.slice(last, match.index) });
    }

    const token = match[0];
    let type = "symbol";

    if (/^\d/.test(token)) type = "number";
    else if (/^[A-Za-z]/.test(token)) type = "identifier";
    else if (/^\\[a-zA-Z]+/.test(token)) type = "symbol";
    else if (/^[+\-×/÷=≈≤≥≠±→←⇌↔]$/.test(token) || /^\\times|\\div$/.test(token)) type = "operator";
    else if (/^[()[\]{},;:]$/.test(token)) type = "punctuation";

    parts.push({ type, value: token });
    last = match.index + token.length;
  }

  if (last < source.length) {
    parts.push({ type: "text", value: source.slice(last) });
  }

  return parts;
}

function FallbackLine({ line, index }) {
  const tokens = tokenizeFallback(line);

  return (
    <div className={getLineClass(line)} key={`math-fallback-line-${index}`}>
      {tokens.map((token, tokenIndex) => (
        <span
          className={`answer-math-token answer-math-token-${token.type}`}
          key={`math-fallback-token-${index}-${tokenIndex}`}
        >
          {token.value}
        </span>
      ))}
    </div>
  );
}


function PlainMathNoteLine({ line, index }) {
  const value = String(line || "").trim();

  const className =
    /^(?:therefore|answer|result|final|conclusion)\b/i.test(value)
      ? "answer-math-line answer-math-line-result"
      : looksLikeMathNoteLine(value)
      ? "answer-math-line answer-math-line-note"
      : "answer-math-line answer-math-line-note";

  return (
    <div
      className={className}
      key={`math-note-line-${index}`}
    >
      {value}
    </div>
  );
}

function SafeBlockMath({ latex, original, index }) {
  /*
    react-katex / KaTeX can throw for invalid syntax. This prevents the whole
    answer from becoming blank if Lexa emits imperfect math.
  */
  try {
    return (
      <div className={getLineClass(latex)} key={`math-katex-line-${index}`}>
        <BlockMath math={latex} errorColor="#dc2626" />
      </div>
    );
  } catch {
    return <FallbackLine line={original || latex} index={index} />;
  }
}

function buildReadableSummary(kind, content) {
  const lower = String(content || "").toLowerCase();

  if (kind.includes("Chemistry")) {
    if (/rightleftharpoons|⇌|<->/.test(content)) return "Equilibrium reaction / chemistry notation";
    if (/to|→|->/.test(content)) return "Chemical reaction / stoichiometry notation";
    return "Chemistry notation";
  }

  if (kind.includes("Finance")) {
    if (lower.includes("assets") && lower.includes("liabilities")) return "Accounting equation";
    if (lower.includes("roi")) return "Return on investment formula";
    if (lower.includes("present value") || /\bpv\b/.test(lower)) return "Time value of money formula";
    return "Finance/accounting formula";
  }

  if (kind.includes("Physics")) return "Physics/science formula";
  if (kind.includes("Calculus")) return "Calculus expression";
  if (kind.includes("Statistics")) return "Statistics/probability expression";

  return "";
}

function normalizeContent(value) {
  let out = stripOuterMarker(value)
    .replace(/\r\n/g, "\n")
    .replace(/\t/g, "  ")
    .trim();

  // If the whole card is one fenced display math block, remove the outer fence
  // before line splitting. Multi-block content is handled by splitMathLines().
  out = out.replace(/^\s*\\\[\s*\n?([\s\S]*?)\n?\s*\\\]\s*$/m, "$1").trim();
  out = out.replace(/^\s*\$\$\s*\n?([\s\S]*?)\n?\s*\$\$\s*$/m, "$1").trim();
  out = out.replace(/^\s*\\\(([\s\S]*?)\\\)\s*$/m, "$1").trim();

  return out;
}

export default function AnswerMath({ value }) {
  const rawContent = useMemo(
    () => normalizeContent(value),
    [value]
  );

  const rawLines = useMemo(
    () => splitMathLines(rawContent),
    [rawContent]
  );

  const isRenderableMath = useMemo(
    () =>
      isRenderableMathContent(
        value,
        rawContent
      ),
    [rawContent, value]
  );

  const lineModels = useMemo(
    () =>
      rawLines.map((line) => {
        const isExpression =
          looksLikeMathExpressionLine(line);

        return {
          original: line,
          isExpression,
          latex: isExpression
            ? normalizePlainToLatex(line)
            : "",
        };
      }),
    [rawLines]
  );

  const kind = useMemo(
    () => detectMathKind(rawContent),
    [rawContent]
  );

  const summary = useMemo(
    () =>
      buildReadableSummary(
        kind,
        rawContent
      ),
    [kind, rawContent]
  );

  if (!rawContent) return null;

  /*
    Defense in depth:
    AnswerRenderer is the primary classifier. If ordinary prose somehow reaches
    AnswerMath, preserve it with AnswerText instead of manufacturing a math card.
  */
  if (!isRenderableMath) {
    return <AnswerText value={rawContent} />;
  }

  return (
    <div className="answer-math-card">
      <div className="answer-math-label">
        {kind}
      </div>

      {summary ? (
        <div className="answer-math-summary">
          {summary}
        </div>
      ) : null}

      <div
        className="answer-math-content answer-math-content-katex"
        aria-label={`${kind} content`}
      >
        {lineModels.map(
          ({ original, isExpression, latex }, index) => {
            if (!isExpression) {
              return (
                <PlainMathNoteLine
                  key={`math-note-${index}`}
                  line={original}
                  index={index}
                />
              );
            }

            return (
              <SafeBlockMath
                key={`safe-math-${index}`}
                latex={latex}
                original={original}
                index={index}
              />
            );
          }
        )}
      </div>
    </div>
  );
}
